/** /delivery-logs
 *
 * Ops-wide event delivery log search across tenants, driven by the reusable
 * QuerySearch query-builder widget (same contract as /bookings/ and /orders/,
 * over the immutable Event_Delivery_Log table — UUID row ids). The serialized
 * query lives in `?q=`; a tenant picker (`?tenant=12,845`, one or several
 * operators) locks the query's tenant and injects `tour_operator_id`.
 * Server-side pagination + sorting (created/finalized only); all column
 * filtering is delegated to the widget.
 *
 * The grid SEEDS a 7-day `created` window on first visit (no `?q=`, no
 * `?preset=`) — UTC datetime bounds, 00:00 first day to 23:59 today (see
 * createdWindow.ts). That chip is editable/removable like any other, but it's
 * load-bearing for performance: the table is daily-partitioned on created_ts
 * and unindexed lookups rely on the window for partition pruning. Batch
 * `all_matching` actions additionally REQUIRE a time-bounded search
 * (server-enforced 400 otherwise).
 *
 * Batch ops (no delete — it's a log): assign/remove error series ID,
 * redeliver (optionally restricted to delivery methods), and recover (orphaned
 * rows only — finishes what a crashed delivery never attempted; non-orphans in
 * the selection are skipped server-side).
 */
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as edlApi from '@/api/eventDeliveryLogs';
import type { DeliveryLogListItem } from '@/api/eventDeliveryLogs';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchSelection } from '@/api/batch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { countLabelDisplayedRows } from '@/components/DataGrid/CountTotalLabel';
import { useCountMode } from '@/components/DataGrid/useCountMode';
import { useGridCountMode } from '@/hooks/useUserPreferences';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { formatTenantIds } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { AssignSeriesDialog } from './AssignSeriesDialog';
import { createdWindowNode } from './createdWindow';
import { buildColumns } from './deliveryLogsColumns';
import { buildDeliveryLogNamespaces } from './deliveryLogsNamespaces';
import { DeliveryLogSummary } from './overview/DeliveryLogSummary';
import { RedeliverDialog } from './RedeliverDialog';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];

const DEFAULT_HIDDEN = [
  'finalized_ts',
  'supplier_ref',
  'reseller_ref',
  'request_id',
  'tour_id',
  'participant_id',
  'departure',
  'trip_start_ts',
  'rendering_lang',
];

/** Presets store the filter state as the raw `?q=` DSL text — it IS the
 *  canonical form (round-trips through parse/serialize), same as orders. */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        // A group that collapsed to one child is no longer canonical — inline it.
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function DeliveryLogsPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildDeliveryLogNamespaces(), []);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  const pageTenant = usePageTenant({ multiple: true });
  // Drives the preset defaults' tenant COLUMN only (shown unless exactly one
  // tenant is named); the filtering value is resolved below, once ?q= has been
  // parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;

  const presets = useGridPresets({
    grid: 'delivery_logs',
    // The tenant column exists unless exactly one tenant is picked;
    // reconciliation in the hook tolerates it appearing/disappearing.
    defaultOrder: useMemo(
      () => buildColumns({ showTenant: defaultShowTenant }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  // Parse the URL query into a tree (tolerant — hand-edited URLs are valid).
  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  // Surface parse diagnostics once per distinct query (hand-edited-URL case).
  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= — typed, or carried by a loaded preset —
  // already names a tenant. The ambient console scope then stands down rather
  // than AND-ing a second, conflicting tenant onto the query. An explicitly
  // PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  // Reset to page 0 whenever the query or tenant changes.
  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      // Editing the query diverges a loaded preset into custom mode.
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  // Seed the default bounded `created` window on first visit — only when
  // neither an explicit query nor a preset drives the page. Written through
  // setQuery so it round-trips as a normal editable chip.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (sp.get('q') !== null || sp.get('preset') !== null) return;
    setQuery([createdWindowNode()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a tenant is picked and the tree still carries tenant conditions,
  // strip them and rewrite ?q= (the picker is authoritative for tenant).
  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the
    // user's (or a preset's) query — that would also diverge a loaded preset.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const onTenantChange = pageTenant.setTenants;

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The suggestion endpoints here are the bookings autocompletes, which take the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // Row-total mode, part 1: the persisted override. Read before the query
  // because it belongs in the query key — switching modes must re-issue the
  // request. Part 2 (useCountMode) needs the response and comes after.
  const countPref = useGridCountMode('delivery_logs');

  // The filter identity of the current query, shared by the list call and the
  // count-only call so the total can never describe a different set than the
  // rows on screen.
  const countOnlyParams = useMemo(
    () => ({
      ordering,
      ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
      filters: toWireAst(tree),
    }),
    [ordering, tenantApiValue, tree],
  );

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'delivery_logs' });
  const query = useQuery({
    queryKey: [
      'delivery-logs',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
      countPref.mode,
    ],
    queryFn: () =>
      edlApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        filters: toWireAst(tree),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        // Only when the user overrode the mode; omitting it keeps the backend's
        // own (deferred) default, which is what makes a cold load free.
        ...(countPref.mode ? { count_mode: countPref.mode } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    // While ?preset= is set but its settings haven't loaded, the effective
    // filters/ordering aren't known yet — don't fire a default-state fetch.
    enabled: !presets.blocking,
  });

  // -------------------------------------------------------------------------
  // Batch operations (series assign/remove + redeliver + recover; NO delete — log
  // model). This grid is a raw DataGridPro (not ResourceDataGrid), so the
  // batch layer is wired here by hand, mirroring OrdersPage — with STRING
  // (UUID) row ids throughout. Cross-page selection works like every other
  // grid: controlled rowSelectionModel + keepNonExistentRowsSelected carry
  // ticks across pages, and the SelectAllBanner offers "Select all matching".
  // -------------------------------------------------------------------------
  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  // Reset the all-matching flag whenever the underlying query changes (the
  // "matching" set is no longer what the user opted into).
  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue, ordering, pageSize: pagination.pageSize }),
    [q, tenantApiValue, ordering, pagination.pageSize],
  );

  // Row totals. This grid defers its count server-side, so `count` arrives as
  // -1 and the pager renders "Show total" instead of a number; MUI paginates on
  // `paginationMeta.hasNextPage`. See components/DataGrid/useCountMode.ts.
  const count = useCountMode({
    pref: countPref,
    queryIdentity,
    response: query.data,
    countOnly: () => edlApi.count(countOnlyParams),
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
    ...(query.data ? { rowsOnPage: query.data.results.length } : {}),
    onResetPage: () => setPagination((p) => ({ ...p, page: 0 })),
  });
  const rowCount = count.rowCount;
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  // UUID string ids (never Number() them — they aren't numeric).
  const selectedIds = useMemo(() => selectionModel.map((i) => String(i)), [selectionModel]);
  const selectedCount = allMatching ? (count.total ?? selectedIds.length) : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  // The list query string the server must re-run for all_matching — the SAME
  // params edlApi.list sends (ordering + tour_operator_id + filters), so the
  // batch resolves the identical set the grid shows.
  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    const wire = toWireAst(tree);
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tenantApiValue, tree]);

  const batchAction = useBatchAction({
    resource: 'event_delivery_logs',
    queryKey: ['delivery-logs'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  // Declared unconditionally (the Tours/ResourceDataGrid convention): the
  // checkbox column is always available; BatchActionsMenu filters the menu by
  // each action's `permission`, and the backend re-checks regardless.
  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'assign_series',
        label: 'Assign error series ID',
        action: 'assign_series',
        permission: 'console_event_delivery_manage',
        dialog: (ctx) => <AssignSeriesDialog ctx={ctx} />,
      },
      {
        key: 'remove_series',
        label: 'Remove error series ID',
        action: 'remove_series',
        permission: 'console_event_delivery_manage',
        confirmText: (n) =>
          `Remove the error series ID from ${n.toLocaleString()} delivery log(s)? This runs in the background.`,
      },
      {
        key: 'redeliver',
        label: 'Redeliver…',
        action: 'redeliver',
        permission: 'console_event_delivery_manage',
        dialog: (ctx) => <RedeliverDialog ctx={ctx} />,
      },
      {
        key: 'recover',
        label: 'Recover orphaned',
        action: 'recover',
        permission: 'console_event_delivery_manage',
        confirmText: (n) =>
          `Recover orphaned deliveries among ${n.toLocaleString()} selected row(s)? ` +
          'Each orphan re-dispatches only the notifications and delivery methods its ' +
          'crashed delivery never attempted, replaying the archived context and matching ' +
          'decisions. Rows that are not orphaned are skipped. This runs in the background.',
      },
    ],
    [],
  );

  const batchEnabled = batchActions.length > 0;

  const columns = useMemo(
    () => orderColumns(buildColumns({ showTenant: tenants.length !== 1 }), presets.orderedFields),
    [tenants.length, presets.orderedFields],
  );

  // Master-detail: expanding a row shows the read-only delivery summary inline
  // (same component as the /delivery-logs/:id Overview tab, with its own header).
  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<DeliveryLogListItem>) => (
      <Box sx={{ p: 2 }}>
        <DeliveryLogSummary logId={row.id} />
      </Box>
    ),
    [],
  );
  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="delivery-logs"
        title="Delivery Logs"
        subtitle="Search finalized event deliveries across every tenant with a tokenized query."
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <TenantPicker multiple value={tenants} onChange={onTenantChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder="Search delivery logs…  e.g. status=error & event=booking.created"
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}

      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<DeliveryLogListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={rowCount}
          {...(count.paginationMeta ? { paginationMeta: count.paginationMeta } : {})}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          getRowHeight={() => 'auto'}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          // DataGridPro does NOT paginate by default (unlike the community
          // grid); without `pagination` it renders a single page with no pager
          // footer, so page/page-size controls never appear.
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection={batchEnabled}
          disableRowSelectionOnClick
          disableColumnFilter
          {...(batchEnabled
            ? {
                rowSelectionModel: selectionModel,
                onRowSelectionModelChange: handleSelectionChange,
                keepNonExistentRowsSelected: true,
              }
            : {})}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch
                  ? 'No delivery logs match this search.'
                  : 'Search to find delivery logs.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            pagination: {
              labelDisplayedRows: countLabelDisplayedRows({
                state: count.state,
                total: count.total,
                canRequest: true,
                onRequest: count.requestTotal,
              }),
            },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'delivery-logs',
              presets: presets.toolbar,
              countMode: { mode: count.mode, setMode: count.setMode },
              ...(batchEnabled
                ? {
                    batch: {
                      actions: batchActions,
                      ids: selectedIds,
                      allMatching,
                      count: selectedCount,
                      matched: count.total,
                      filterQuery,
                      runStandard,
                      clearSelection,
                      onSelectAllMatching: () => setAllMatching(true),
                      isPending: batchAction.isPending,
                    },
                  }
                : {}),
            } as GridToolbarProps,
          }}
        />
      )}

      {query.data?.count_is_capped && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          10,000+ results — refine your search to see more precise counts.
        </Typography>
      )}
    </Box>
  );
}
